import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AtSign, Loader2, Lock, MessageSquare, Send, StickyNote, Undo2, X, Pencil, Check, Trash2 } from "lucide-react";
import api from "@/lib/api";
import ReviewPanel from "@/components/tracker/ReviewPanel";
import { useBilling } from "@/context/BillingContext";
import { useAuth } from "@/context/AuthContext";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { formatApiError, timeAgo } from "@/lib/constants";

function renderMentions(text, mentions = []) {
  if (!mentions || mentions.length === 0) return text;
  const escaped = mentions.map((m) => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`@(?:${escaped.join("|")})`, "g");
  const parts = [];
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <span key={match.index} className="font-bold text-[#90C2E7]">{match[0]}</span>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Set by TaskDetail right before it hands over to the discussion window.
export const handoff = { at: 0 };

export default function NoteViewer({ task, canEdit = false, canReply = false, canReview = false, onSaved, onClose }) {
  const { sub, has } = useBilling();
  const { user } = useAuth();
  const canDiscuss = has("discussions");
  const discussionPlan = (sub && sub.plans ? sub.plans.find((p) => (p.features || []).includes("discussions")) : null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [ready, setReady] = useState(false);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editMentionQuery, setEditMentionQuery] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [openOriginalId, setOpenOriginalId] = useState(null);
  const [noteHistory, setNoteHistory] = useState([]);
  const replyRef = useRef(null);
  const editRef = useRef(null);
  const downOnBackdrop = useRef(false);

  useEffect(() => {
    setDraft(task?.notes || "");
    setReply("");
    setSelectedMentions([]);
    setMentionQuery(null);
    setNoteHistory([]);
    setSaving(false);
    setPosting(false);
    setComments([]);
    setReady(false);
    if (!task?.id) return undefined;
    // Wait (briefly) for replies + note history so the window animates in once at full height.
    let done = false;
    const finish = () => { if (!done) { done = true; setReady(true); } };
    const fallback = setTimeout(finish, 800);
    Promise.all([
      api.get(`/tasks/${task.id}`).then((r) => setNoteHistory(r.data.note_history || [])).catch(() => {}),
      api.get(`/tasks/${task.id}/comments`).then((r) => setComments(r.data)).catch(() => {}),
    ]).finally(finish);
    api.post(`/tasks/${task.id}/comments/seen`).catch(() => {});
    return () => { done = true; clearTimeout(fallback); };
    // reload only when a different task opens; re-running on note edits would wipe the draft
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  useEffect(() => {
    if (task && canReply && canDiscuss) {
      api.get("/users/directory").then((res) => setDirectory(res.data)).catch(() => {});
    }
  }, [task, canReply, canDiscuss]);

  const loadComments = useCallback(() => {
    if (task) {
      api.get(`/tasks/${task.id}/comments`).then((res) => setComments(res.data)).catch(() => {});
    }
  }, [task]);

  // Keep an open discussion live: poll every 10s and on window focus so a teammate's
  // reply shows up without reopening the task.
  useLiveRefresh(loadComments, { interval: 10000 });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}`, { notes: draft });
      toast.success("Note saved");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (text) => {
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}`, { notes: text });
      toast.success("Previous version restored");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const filteredMentions =
    mentionQuery === null
      ? []
      : directory
          .filter((u) => u.name.toLowerCase().includes(mentionQuery.toLowerCase()))
          .slice(0, 6);

  const onReplyChange = (e) => {
    const value = e.target.value;
    setReply(value);
    const upToCursor = value.slice(0, e.target.selectionStart);
    const m = upToCursor.match(/@([A-Za-z]{0,20})$/);
    setMentionQuery(m ? m[1] : null);
  };

  const selectMention = (u) => {
    setReply((prev) => prev.replace(/@([A-Za-z]{0,20})$/, `@${u.name} `));
    setSelectedMentions((prev) => (prev.some((m) => m.id === u.id) ? prev : [...prev, u]));
    setMentionQuery(null);
    replyRef.current?.focus();
  };

  const handlePost = async () => {
    if (posting || !reply.trim()) return;
    const mentions = selectedMentions.filter((m) => reply.includes(`@${m.name}`)).map((m) => m.id);
    setPosting(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, { text: reply.trim(), mentions });
      setReply("");
      setSelectedMentions([]);
      setMentionQuery(null);
      loadComments();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  const handleEditStart = (c) => {
    setConfirmDeleteId(null);
    setOpenOriginalId(null);
    setEditingId(c.id);
    setEditText(c.text);
    setEditMentionQuery(null);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditText("");
    setEditMentionQuery(null);
  };

  const onEditChange = (e) => {
    const value = e.target.value;
    setEditText(value);
    const upToCursor = value.slice(0, e.target.selectionStart);
    const m = upToCursor.match(/@([A-Za-z]{0,20})$/);
    setEditMentionQuery(m ? m[1] : null);
  };

  const editFilteredMentions =
    editMentionQuery === null
      ? []
      : directory.filter((u) => u.name.toLowerCase().includes(editMentionQuery.toLowerCase())).slice(0, 6);

  const selectEditMention = (u) => {
    setEditText((prev) => prev.replace(/@([A-Za-z]{0,20})$/, `@${u.name} `));
    setEditMentionQuery(null);
    editRef.current?.focus();
  };

  const handleEditSave = async (c) => {
    const text = editText.trim();
    if (!text || editSaving) return;
    const source = directory.length ? directory : (c.mentions || []);
    const mentions = source.filter((u) => text.includes(`@${u.name}`)).map((u) => u.id);
    setEditSaving(true);
    try {
      await api.patch(`/tasks/${task.id}/comments/${c.id}`, { text, mentions });
      setEditingId(null);
      setEditText("");
      setEditMentionQuery(null);
      loadComments();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const handleRestoreReply = async (c) => {
    try {
      await api.post(`/tasks/${task.id}/comments/${c.id}/restore`);
      toast.success("Reply restored");
      loadComments();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (c) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/tasks/${task.id}/comments/${c.id}`);
      setConfirmDeleteId(null);
      loadComments();
      onSaved?.();
      toast.success("Reply deleted", {
        duration: 6000,
        action: { label: "Undo", onClick: () => handleRestoreReply(c) },
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AnimatePresence>
      {task && (
        <motion.div
          initial={Date.now() - handoff.at < 500 ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (downOnBackdrop.current && e.target === e.currentTarget) onClose(); }}
        >
          {ready && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0d0f12] p-8 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
            data-testid="task-note-viewer"
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#00FF66]">
                <MessageSquare className="h-4 w-4" />
                Task discussion
              </span>
              <button
                onClick={onClose}
                className="text-white/40 transition-colors duration-200 hover:text-white"
                data-testid="task-note-viewer-close"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <h3 className="font-display text-lg font-bold">{task.title}</h3>

            <ReviewPanel task={task} canEdit={canReview} onSaved={onSaved} />

            <p className="mt-5 mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
              <StickyNote className="h-3 w-3" />
              Main note
            </p>
            {canEdit ? (
              <>
                <textarea
                  rows={3}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Leave an update for the team…"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm leading-relaxed text-white outline-none transition-colors duration-200 focus:border-[#00FF66]/60"
                  data-testid="note-editor-input"
                />
                <motion.button
                  onClick={handleSave}
                  disabled={saving}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#00FF66] py-2.5 text-xs font-bold text-black disabled:opacity-60"
                  data-testid="note-save-button"
                >
                  {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Save note
                </motion.button>
              </>
            ) : (
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-white/65" data-testid="task-note-viewer-text">
                {task.notes || "No note yet."}
              </p>
            )}

            {noteHistory.length > 0 && (
              <div className="mt-5 border-t border-white/10 pt-4" data-testid="note-history-section">
                <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">Previous versions</p>
                <ul className="space-y-3">
                  {[...noteHistory].reverse().map((h, i) => (
                    <li key={i} className="rounded-xl bg-white/[0.04] px-4 py-3" data-testid={`note-history-${i}`}>
                      <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-white/50">{h.notes}</p>
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <p className="text-[10px] font-semibold text-white/30">
                          Replaced by {h.edited_by} · {timeAgo(h.edited_at)}
                        </p>
                        {canEdit && (
                          <button
                            onClick={() => handleRestore(h.notes)}
                            disabled={saving}
                            className="flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white/50 transition-colors duration-200 hover:border-[#00FF66]/50 hover:text-[#00FF66] disabled:opacity-50"
                            data-testid={`note-restore-${i}`}
                          >
                            <Undo2 className="h-3 w-3" />
                            Restore
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 border-t border-white/10 pt-4">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                Replies {comments.length > 0 && `· ${comments.length}`}
              </p>
              {comments.length > 0 && (
                <ul className="mb-4 space-y-2.5" data-testid="comments-thread">
                  {comments.map((c) => {
                    const isAdmin = c.author_role === "admin";
                    const mine = user && c.author_id === user.id;
                    const isEditing = editingId === c.id;
                    const isConfirmingDelete = confirmDeleteId === c.id;
                    const history = (c.edit_history && c.edit_history.length)
                      ? c.edit_history
                      : (c.original_text ? [{ text: c.original_text, at: c.created_at }] : []);
                    const hasHistory = !!c.edited_at && history.length > 0;

                    if (c.deleted) {
                      return (
                        <li
                          key={c.id}
                          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3"
                          data-testid={`comment-${c.id}`}
                        >
                          <p className="flex items-center gap-2 text-xs italic text-white/30" data-testid={`comment-deleted-${c.id}`}>
                            <Trash2 className="h-3 w-3" />
                            {c.author_name} deleted this reply
                            <span className="ml-auto not-italic text-[10px] text-white/25">{timeAgo(c.deleted_at || c.created_at)}</span>
                          </p>
                        </li>
                      );
                    }

                    return (
                      <li
                        key={c.id}
                        className={`rounded-2xl border px-4 py-3 ${
                          isAdmin ? "border-[#00FF66]/25 bg-[#00FF66]/[0.07]" : "border-[#D8B4E2]/25 bg-[#D8B4E2]/[0.07]"
                        }`}
                        data-testid={`comment-${c.id}`}
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <span className={`text-[10px] font-black uppercase tracking-widest ${isAdmin ? "text-[#00FF66]" : "text-[#D8B4E2]"}`}>
                            {c.author_name}
                          </span>
                          <span className="text-[9px] font-bold uppercase tracking-wider text-white/25">
                            {isAdmin ? "Admin" : (task && c.author_id === task.owner_id ? "Owner" : "Member")}
                          </span>
                          {c.edited_at && (
                            hasHistory ? (
                              <button
                                type="button"
                                onClick={() => setOpenOriginalId(openOriginalId === c.id ? null : c.id)}
                                className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                                  openOriginalId === c.id ? "bg-white/10 text-white/70" : "text-white/30 hover:text-white/60"
                                }`}
                                data-testid={`comment-edited-badge-${c.id}`}
                              >
                                <Pencil className="h-2.5 w-2.5" /> edited{history.length > 1 ? ` · ${history.length}` : ""}
                              </button>
                            ) : (
                              <span
                                className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-white/30"
                                title={`Edited ${new Date(c.edited_at).toLocaleString()}`}
                                data-testid={`comment-edited-badge-${c.id}`}
                              >
                                <Pencil className="h-2.5 w-2.5" /> edited
                              </span>
                            )
                          )}
                          <span className="ml-auto text-[10px] text-white/30">{timeAgo(c.created_at)}</span>
                          {mine && canReply && !isEditing && !isConfirmingDelete && (
                            <div className="flex items-center gap-1.5">
                              {canDiscuss && (
                                <button
                                  onClick={() => handleEditStart(c)}
                                  className="text-white/30 transition-colors duration-200 hover:text-white"
                                  title="Edit your reply"
                                  aria-label="Edit reply"
                                  data-testid={`comment-edit-${c.id}`}
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              )}
                              <button
                                onClick={() => { setEditingId(null); setConfirmDeleteId(c.id); }}
                                className="text-white/30 transition-colors duration-200 hover:text-[#FF8B94]"
                                title="Delete your reply"
                                aria-label="Delete reply"
                                data-testid={`comment-delete-${c.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        {isEditing ? (
                          <div className="relative space-y-2">
                            {editMentionQuery !== null && editFilteredMentions.length > 0 && (
                              <div className="absolute bottom-full left-0 z-10 mb-2 w-64 overflow-hidden rounded-xl border border-white/10 bg-[#0E1712] shadow-xl" data-testid="edit-mention-dropdown">
                                <p className="flex items-center gap-1 border-b border-white/5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-white/30">
                                  <AtSign className="h-2.5 w-2.5" /> Tag a teammate
                                </p>
                                <ul>
                                  {editFilteredMentions.map((u) => (
                                    <li key={u.id}>
                                      <button
                                        onClick={() => selectEditMention(u)}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-white/75 transition-colors duration-150 hover:bg-white/5"
                                        data-testid={`edit-mention-option-${u.id}`}
                                      >
                                        <AtSign className="h-3 w-3 text-[#90C2E7]" /> {u.name}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <textarea
                              ref={editRef}
                              value={editText}
                              onChange={onEditChange}
                              rows={3}
                              autoFocus
                              className="w-full resize-none rounded-xl border border-white/15 bg-black/30 px-3 py-2 text-xs leading-relaxed text-white/85 outline-none transition-colors duration-200 focus:border-[#00FF66]/50"
                              data-testid={`comment-edit-input-${c.id}`}
                            />
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEditSave(c)}
                                disabled={editSaving || !editText.trim()}
                                className="flex items-center gap-1 rounded-full bg-[#00FF66] px-3 py-1 text-[10px] font-bold text-black transition-transform duration-200 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                                data-testid={`comment-edit-save-${c.id}`}
                              >
                                {editSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                              </button>
                              <button
                                onClick={handleEditCancel}
                                disabled={editSaving}
                                className="rounded-full border border-white/15 px-3 py-1 text-[10px] font-bold text-white/60 transition-colors duration-200 hover:text-white disabled:opacity-50"
                                data-testid={`comment-edit-cancel-${c.id}`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-white/75">{renderMentions(c.text, c.mentions)}</p>
                            <AnimatePresence initial={false}>
                              {openOriginalId === c.id && hasHistory && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                  className="overflow-hidden"
                                >
                                  <div className="mt-2.5 rounded-xl border border-white/10 bg-black/25 px-3 py-2.5" data-testid={`comment-original-${c.id}`}>
                                    <p className="mb-2 text-[9px] font-bold uppercase tracking-widest text-white/35">
                                      Edit history · {history.length} previous {history.length === 1 ? "version" : "versions"}
                                    </p>
                                    <ul className="space-y-2.5">
                                      {history.map((h, i) => (
                                        <li key={i} className="border-l-2 border-white/10 pl-2.5">
                                          <div className="mb-0.5 flex items-center gap-1.5">
                                            <span className="text-[8px] font-bold uppercase tracking-widest text-white/40">
                                              {i === 0 ? "Original" : "Edited"}
                                            </span>
                                            <span className="text-[9px] text-white/25">{new Date(h.at).toLocaleString()}</span>
                                          </div>
                                          <p className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-white/55">{h.text}</p>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                            {isConfirmingDelete && (
                              <div className="mt-2.5 flex items-center gap-2 rounded-xl border border-[#FF8B94]/25 bg-[#FF8B94]/[0.06] px-3 py-2">
                                <span className="flex-1 text-[11px] text-white/60">Delete this reply?</span>
                                <button
                                  onClick={() => handleDelete(c)}
                                  disabled={deleting}
                                  className="flex items-center gap-1 rounded-full bg-[#FF8B94] px-3 py-1 text-[10px] font-bold text-black transition-transform duration-200 hover:scale-[1.03] disabled:opacity-50"
                                  data-testid={`comment-delete-confirm-${c.id}`}
                                >
                                  {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Delete
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  disabled={deleting}
                                  className="rounded-full border border-white/15 px-3 py-1 text-[10px] font-bold text-white/60 transition-colors duration-200 hover:text-white disabled:opacity-50"
                                  data-testid={`comment-delete-cancel-${c.id}`}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {!canDiscuss ? (
                <div
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                  data-testid="discussion-upgrade-prompt"
                >
                  <div className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-black/40">
                    <Lock className="h-4 w-4 text-[#00FF66]" />
                  </div>
                  <p className="text-sm font-semibold text-white/80">
                    {comments.length > 0 ? "Replies are read-only on your plan." : `Task discussions are a ${discussionPlan?.name || "Team"} feature.`}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-white/45">
                    {comments.length > 0
                      ? `Upgrade to ${discussionPlan?.name || "Team"} to reply and @mention teammates again. Your existing thread stays here.`
                      : "Reply to teammates and tag them with @mentions to keep every decision on the task."}
                  </p>
                  <Link
                    to="/app/billing"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#00FF66] px-4 py-2 text-xs font-bold text-black transition-transform duration-200 hover:scale-[1.03]"
                    data-testid="discussion-upgrade-button"
                  >
                    Upgrade to {discussionPlan?.name || "Team"}
                  </Link>
                </div>
              ) : canReply ? (
                <div className="relative flex items-end gap-2">
                  {mentionQuery !== null && filteredMentions.length > 0 && (
                    <div
                      className="absolute bottom-full left-0 z-10 mb-2 w-64 overflow-hidden rounded-xl border border-white/10 bg-[#0E1712] shadow-xl"
                      data-testid="mention-dropdown"
                    >
                      <p className="flex items-center gap-1 border-b border-white/5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-white/30">
                        <AtSign className="h-2.5 w-2.5" /> Tag a teammate
                      </p>
                      <ul>
                        {filteredMentions.map((u) => (
                          <li key={u.id}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectMention(u);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-150 hover:bg-white/[0.06]"
                              data-testid={`mention-option-${u.id}`}
                            >
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#90C2E7]/15 text-[10px] font-black text-[#90C2E7]">
                                {u.name.charAt(0)}
                              </span>
                              <span className="text-xs font-semibold text-white/80">{u.name}</span>
                              <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-white/25">{u.role}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <textarea
                    ref={replyRef}
                    rows={2}
                    value={reply}
                    onChange={onReplyChange}
                    onKeyDown={(e) => {
                      if (e.key === "Escape" && mentionQuery !== null) {
                        setMentionQuery(null);
                        return;
                      }
                      if (e.key === "Enter" && !e.shiftKey) {
                        if (mentionQuery !== null && filteredMentions.length > 0) {
                          e.preventDefault();
                          selectMention(filteredMentions[0]);
                          return;
                        }
                        e.preventDefault();
                        handlePost();
                      }
                    }}
                    placeholder="Reply — type @ to tag a teammate…"
                    className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm leading-relaxed text-white outline-none transition-colors duration-200 focus:border-[#00FF66]/60"
                    data-testid="comment-input"
                  />
                  <motion.button
                    onClick={handlePost}
                    disabled={posting || !reply.trim()}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#00FF66] text-black disabled:opacity-50"
                    data-testid="comment-send-button"
                    aria-label="Send reply"
                  >
                    {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </motion.button>
                </div>
              ) : (
                comments.length === 0 && (
                  <p className="text-xs text-white/30">No replies yet. Only members of this project can reply.</p>
                )
              )}
            </div>
          </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
